// Calendario appuntamenti CAF (ISEE / 730): settimana da lunedì a sabato, una riga per fascia oraria.
// Casella libera → nuovo appuntamento; appuntamento → dettaglio (sposta / annulla / fatto).
// Gli orari di apertura (durata, posti, giorni, chiusure) si impostano da "Orari" (amministratori).
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ChevronLeft, ChevronRight, Plus, Settings2, Smartphone, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { NuovoAppuntamento, DettaglioAppuntamento, COLORE_TIPO, TIPI_APP } from '../components/caf/Appuntamenti';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NOMI = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const lunediDi = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };

function fasce(orari, giornoSettimana) {
  const out = [];
  (orari.settimana?.[String(giornoSettimana)] || []).forEach(([da, a]) => {
    let [h, m] = da.split(':').map(Number);
    const [hf, mf] = a.split(':').map(Number);
    while (h * 60 + m + orari.durata <= hf * 60 + mf) {
      out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      m += orari.durata; h += Math.floor(m / 60); m %= 60;
    }
  });
  return out;
}

function ImpostaOrari({ orari, onChiudi, onSalvati }) {
  const [o, setO] = useState(JSON.parse(JSON.stringify(orari)));
  const [chiusura, setChiusura] = useState('');
  const fascia = (g, i, k, v) => { const n = { ...o, settimana: { ...o.settimana } }; n.settimana[g] = n.settimana[g].map((f, j) => (j === i ? (k === 0 ? [v, f[1]] : [f[0], v]) : f)); setO(n); };
  const salva = async () => {
    try { const r = await axios.put(`${API}/appuntamenti/orari`, o); toast.success('Orari salvati'); onSalvati(r.data); }
    catch (err) { toast.error(err.response?.data?.detail || 'Orari non salvati'); }
  };
  return (
    <Dialog open onOpenChange={(x) => { if (!x) onChiudi(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Orari degli appuntamenti</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label>Durata (min)</Label><Input type="number" value={o.durata} onChange={(e) => setO({ ...o, durata: Number(e.target.value) })} /></div>
          <div><Label>Clienti per orario</Label><Input type="number" value={o.posti} onChange={(e) => setO({ ...o, posti: Number(e.target.value) })} /></div>
          <div><Label>Anticipo app (ore)</Label><Input type="number" value={o.anticipo_ore} onChange={(e) => setO({ ...o, anticipo_ore: Number(e.target.value) })} /></div>
          <div><Label>Prenotabili (giorni)</Label><Input type="number" value={o.giorni_prenotabili} onChange={(e) => setO({ ...o, giorni_prenotabili: Number(e.target.value) })} /></div>
        </div>
        <div className="rounded-lg border p-3 space-y-3 bg-slate-50 mt-2">
          <div className="font-semibold text-sm">Prenotazioni dall'app per servizio</div>
          <p className="text-xs text-slate-500 -mt-2">Dal CRM lo studio può fissare appuntamenti sempre; qui decidi quando i clienti possono prenotare da soli.</p>
          {Object.entries(TIPI_APP).map(([k, nome]) => {
            const t = o.tipi?.[k] || { aperto: true, dal: '', al: '', messaggio: '' };
            const imposta = (campi) => setO({ ...o, tipi: { ...(o.tipi || {}), [k]: { ...t, ...campi } } });
            return (
              <div key={k} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end border-t pt-2 first:border-t-0 first:pt-0">
                <label className="flex items-center gap-2 text-sm font-medium md:pb-2">
                  <input type="checkbox" checked={t.aperto} onChange={(e) => imposta({ aperto: e.target.checked })} />
                  {nome}: {t.aperto ? <span className="text-green-700">aperte</span> : <span className="text-red-600">chiuse</span>}
                </label>
                <div><Label className="text-xs">Dal (facoltativo)</Label><Input type="date" value={t.dal} onChange={(e) => imposta({ dal: e.target.value })} /></div>
                <div><Label className="text-xs">Al / termine (facoltativo)</Label><Input type="date" value={t.al} min={t.dal || undefined} onChange={(e) => imposta({ al: e.target.value })} /></div>
                <div><Label className="text-xs">Messaggio quando chiuse</Label><Input value={t.messaggio} maxLength={300} placeholder={`es. Le prenotazioni ${nome} riaprono…`} onChange={(e) => imposta({ messaggio: e.target.value })} /></div>
              </div>
            );
          })}
        </div>
        <div className="space-y-2 mt-2">
          {[0, 1, 2, 3, 4, 5, 6].map((g) => (
            <div key={g} className="flex flex-wrap items-center gap-2 border-b pb-2">
              <span className="w-24 font-medium">{['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'][g]}</span>
              {(o.settimana[String(g)] || []).length === 0 && <span className="text-sm text-slate-400">chiuso</span>}
              {(o.settimana[String(g)] || []).map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1 border rounded-md px-1.5 py-1 bg-slate-50">
                  <input type="time" value={f[0]} onChange={(e) => fascia(String(g), i, 0, e.target.value)} className="bg-transparent text-sm" />–
                  <input type="time" value={f[1]} onChange={(e) => fascia(String(g), i, 1, e.target.value)} className="bg-transparent text-sm" />
                  <button type="button" onClick={() => setO({ ...o, settimana: { ...o.settimana, [g]: o.settimana[String(g)].filter((_, j) => j !== i) } })}><X className="w-3.5 h-3.5 text-red-600" /></button>
                </span>
              ))}
              <Button type="button" size="sm" variant="ghost" onClick={() => setO({ ...o, settimana: { ...o.settimana, [g]: [...(o.settimana[String(g)] || []), ['09:00', '12:00']] } })}>+ fascia</Button>
            </div>
          ))}
        </div>
        <div className="mt-2">
          <Label>Giorni di chiusura (festivi, ferie)</Label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {(o.chiusure || []).map((c) => (
              <span key={c} className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-800 px-2 py-0.5 text-sm">{c.split('-').reverse().join('/')}
                <button type="button" onClick={() => setO({ ...o, chiusure: o.chiusure.filter((x) => x !== c) })}><X className="w-3 h-3" /></button></span>
            ))}
          </div>
          <div className="flex gap-2 mt-2"><Input type="date" className="w-48" value={chiusura} onChange={(e) => setChiusura(e.target.value)} />
            <Button type="button" variant="outline" disabled={!chiusura} onClick={() => { setO({ ...o, chiusure: [...new Set([...(o.chiusure || []), chiusura])] }); setChiusura(''); }}>Aggiungi chiusura</Button></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onChiudi}>Annulla</Button><Button onClick={salva}>Salva orari</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CalendarioAppuntamenti() {
  const { isAdmin, isSuperAdmin, isMaster } = useAuth();
  const [lunedi, setLunedi] = useState(lunediDi(new Date()));
  const [dati, setDati] = useState(null);
  const [nuovo, setNuovo] = useState(null);       // {inizio}
  const [aperto, setAperto] = useState(null);
  const [orariAperti, setOrariAperti] = useState(false);
  const giorni = [0, 1, 2, 3, 4, 5, 6].map((k) => { const d = new Date(lunedi); d.setDate(d.getDate() + k); return d; });
  const carica = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/appuntamenti`, { params: { dal: iso(giorni[0]), al: iso(giorni[6]) } });
      setDati(r.data);
    } catch (_) { toast.error('Errore nel caricamento del calendario'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lunedi]);
  useEffect(() => { carica(); }, [carica]);
  if (!dati) return <div className="p-10 text-center text-slate-400">Caricamento…</div>;

  const o = dati.orari;
  const aperti = giorni.filter((d) => fasce(o, (d.getDay() + 6) % 7).length > 0 || dati.appuntamenti.some((a) => a.inizio.startsWith(iso(d))));
  const righe = [...new Set(aperti.flatMap((d) => fasce(o, (d.getDay() + 6) % 7)).concat(dati.appuntamenti.map((a) => a.inizio.slice(11))))].sort();
  const adesso = new Date();
  const fatti = { isee: 0, 730: 0 };
  dati.appuntamenti.filter((a) => a.stato === 'fissato').forEach((a) => { fatti[a.tipo] = (fatti[a.tipo] || 0) + 1; });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Appuntamenti CAF</h1>
          <p className="text-slate-600 mt-1">ISEE e 730/740 · i clienti già registrati prenotano anche dall'app</p>
        </div>
        <div className="flex gap-2">
          {(isAdmin() || isSuperAdmin() || isMaster()) && <Button variant="outline" onClick={() => setOrariAperti(true)}><Settings2 className="w-4 h-4 mr-2" /> Orari</Button>}
          <Button onClick={() => setNuovo({ inizio: '' })}><Plus className="w-4 h-4 mr-2" /> Nuovo appuntamento</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => { const d = new Date(lunedi); d.setDate(d.getDate() - 7); setLunedi(d); }}><ChevronLeft className="w-4 h-4" /></Button>
        <Button variant="outline" size="sm" onClick={() => setLunedi(lunediDi(new Date()))}>Oggi</Button>
        <Button variant="outline" size="sm" onClick={() => { const d = new Date(lunedi); d.setDate(d.getDate() + 7); setLunedi(d); }}><ChevronRight className="w-4 h-4" /></Button>
        <span className="font-semibold ml-2">{giorni[0].toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })} – {giorni[5].toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
        <span className="ml-auto flex gap-2 text-xs">
          <span className={`px-2 py-1 rounded border ${COLORE_TIPO.isee}`}>ISEE {fatti.isee}</span>
          <span className={`px-2 py-1 rounded border ${COLORE_TIPO[730]}`}>730/740 {fatti[730]}</span>
        </span>
      </div>

      <div className="overflow-x-auto bg-white rounded-xl border">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-slate-50">
              <th className="p-2 w-16 border-b border-slate-900" />
              {aperti.map((d) => (
                <th key={iso(d)} className={`p-2 border-b border-slate-900 text-left min-w-[150px] ${iso(d) === iso(adesso) ? 'bg-blue-50' : ''}`}>
                  {NOMI[(d.getDay() + 6) % 7]} {d.getDate()}/{d.getMonth() + 1}
                  {(o.chiusure || []).includes(iso(d)) && <span className="ml-1 text-xs text-red-600">chiuso</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {righe.map((ora) => (
              <tr key={ora}>
                <td className="p-1.5 text-xs text-slate-500 border-t align-top">{ora}</td>
                {aperti.map((d) => {
                  const inizio = `${iso(d)}T${ora}`;
                  const qui = dati.appuntamenti.filter((a) => a.inizio === inizio);
                  const attivi = qui.filter((a) => a.stato === 'fissato');
                  const disponibile = fasce(o, (d.getDay() + 6) % 7).includes(ora) && !(o.chiusure || []).includes(iso(d))
                    && new Date(inizio) > adesso && attivi.length < o.posti;
                  return (
                    <td key={inizio} className={`p-1 border-t border-l align-top ${disponibile ? 'hover:bg-green-50 cursor-pointer' : ''}`}
                      onClick={() => { if (disponibile) setNuovo({ inizio }); }}>
                      {qui.map((a) => (
                        <button key={a.id} type="button" onClick={(e) => { e.stopPropagation(); setAperto(a); }}
                          className={`w-full text-left rounded-md border px-2 py-1 mb-1 ${a.stato === 'fissato' ? COLORE_TIPO[a.tipo] : 'bg-slate-50 text-slate-400 line-through border-slate-200'} ${a.stato === 'completato' ? 'opacity-60' : ''}`}>
                          <div className="text-[11px] font-semibold flex items-center gap-1">{TIPI_APP[a.tipo]}{a.creato_da === 'cliente' && <Smartphone className="w-3 h-3" />}</div>
                          <div className="truncate">{a.client_name}</div>
                        </button>
                      ))}
                      {disponibile && qui.length === 0 && <div className="text-[11px] text-slate-300 px-1">libero</div>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">Clic su una casella "libero" per fissare un appuntamento · <Smartphone className="w-3 h-3 inline" /> = prenotato dal cliente nell'app · barrato = annullato.</p>

      {nuovo && <NuovoAppuntamento inizio={nuovo.inizio} onChiudi={() => setNuovo(null)} onFatto={() => { setNuovo(null); carica(); }} />}
      {aperto && <DettaglioAppuntamento a={aperto} onChiudi={() => setAperto(null)} onFatto={() => { setAperto(null); carica(); }} />}
      {orariAperti && <ImpostaOrari orari={o} onChiudi={() => setOrariAperti(false)} onSalvati={() => { setOrariAperti(false); carica(); }} />}
    </div>
  );
}
